<script setup lang="ts">
import { clamp } from "lodash-es";
import { provide, ref, watch } from "vue";

import { keepBoardScrollPos } from "@/components/utils/boardRect";
import { useNativeEvents } from "@/composables/useNativeEvents";
import { adjustBoardLeftSize } from "@/math/coordinate-utils";
import { useBoardSizeStore } from "@/store/boardSize";
import { useClientSettingsStore } from "@/store/clientSettings";
import { useSidePanelStore } from "@/store/sidePanel";
import { windowSize } from "@/utils/dom/coordinates";

import { sidePanelKey } from "./injectKeys";

const MIN_WIDTH = 400;
const MAX_WIDTH = () => windowSize().x / 2;
const DEFAULT_WIDTH = 420;

const props = defineProps<{
  active: boolean;
  side: "left" | "right";
  resizable?: boolean;
  name?: string;
}>();

const emit = defineEmits<{ close: [] }>();
const close = () => emit("close");

provide(sidePanelKey, { close });

const width = ref(0);
const startX = ref(0);
const resizing = ref(false);

watch(
  () => props.active,
  (active) => setWidth(active ? initialWidth(props.name) : 0),
  { immediate: true },
);

function setWidth(w: number, resizing = false) {
  if (useSidePanelStore().setWidth(props.side, w, close)) {
    width.value = w;
    if (!resizing) {
      if (props.side === "left") {
        useBoardSizeStore().scrollableMarginLocked = w > 0;
        keepBoardScrollPos(() => adjustBoardLeftSize(w));
      }
      if (props.name && w !== 0) {
        useClientSettingsStore().setPanelWidth(props.name, w);
      }
    }
  }
}

function initialWidth(name?: string) {
  return useClientSettingsStore().panelWidth(name || "") || DEFAULT_WIDTH;
}

const { addEventListener, removeEventListeners } = useNativeEvents();

function startResize(event: PointerEvent) {
  resizing.value = true;
  event.stopPropagation();
  startX.value = event.pageX - width.value;
  addEventListener(document, "pointermove", resize);
  addEventListener(document, "pointerup", endResize);
}

function endResize(event: PointerEvent) {
  resizing.value = false;
  removeEventListeners();
  resize(event, true);
}

function resize(event: PointerEvent, end = false) {
  setWidth(clamp(event.pageX - startX.value, MIN_WIDTH, MAX_WIDTH()), !end);
}
</script>

<template>
  <Transition :name="'side-panel-' + side">
    <div v-if="active" class="side-panel" :style="{ [side]: 0, width: width + 'px' }">
      <div class="content">
        <slot />
        <div v-if="resizing" class="capture" />
      </div>
      <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
      <div v-if="resizable" class="resizer" @pointerdown="startResize" />
    </div>
  </Transition>
</template>

<style scoped lang="scss">
@use "@/styles/variables" as vars;
@use "@/styles/variables/colors";
@use "@/styles/side-panel";

.side-panel {
  position: fixed;
  top: vars.$top-header-height;
  bottom: 0;
  background-color: colors.$background-ui;

  .content {
    width: 100%;
    height: 100%;

    // capture pointer events during resizing
    // (otherwise iframes can capture them and we might lose pointerup events)
    .capture {
      position: absolute;
      inset: 0;
    }
  }

  .resizer {
    position: absolute;
    top: 0;
    right: -5px;
    bottom: 0;
    width: 10px;
    cursor: ew-resize;
  }
}

.side-panel-left-enter-active,
.side-panel-left-leave-active,
.side-panel-right-enter-active,
.side-panel-right-leave-active {
  transition:
    transform ease-in-out side-panel.$panel-transition,
    opacity ease-in-out side-panel.$panel-transition;
}

.side-panel-left-enter-from,
.side-panel-left-leave-to {
  opacity: 0;
  transform: translateX(-100%);
}

.side-panel-right-enter-from,
.side-panel-right-leave-to {
  opacity: 0;
  transform: translateX(100%);
}
</style>
